import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { getCollectionThoughts } from '../services/collectionService';
import { getThoughts } from '../services/thoughtService';

/**
 * Custom hook to fetch thoughts for a collection with loading state.
 *
 * @param {string} collectionId - The ID of the collection to fetch thoughts for
 * @param {boolean} isDefault - Whether this is the default "Your Thoughts" collection
 * @returns {Object} - The collection thoughts state and loading state
 */
const useCollectionThoughts = (collectionId, isDefault = false) => {
  const [collectionThoughts, setCollectionThoughts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchCollectionThoughts = async () => {
      setLoading(true);
      setError(null);

      try {
        let fetched;
        if (isDefault) {
          // "Your Thoughts" is EVERY thought the user has — not the home page's
          // drafts-only array. Fetch the full corpus straight from the DB, the
          // same way useAllThoughts does. (It previously used the passed-in home
          // `thoughts` prop, which post-refactor is untriaged-only, so the
          // default collection went empty once everything had been triaged.)
          const { data: { session } } = await supabase.auth.getSession();
          fetched = session?.user ? await getThoughts(session.user.id) : [];
        } else {
          fetched = await getCollectionThoughts(collectionId);
        }
        if (!cancelled) setCollectionThoughts(fetched);
      } catch (err) {
        console.error('Error fetching collection thoughts:', err);
        if (!cancelled) {
          setCollectionThoughts([]);
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCollectionThoughts();
    return () => { cancelled = true; };
  }, [collectionId, isDefault]);

  return {
    collectionThoughts,
    loading,
    error,
    setCollectionThoughts
  };
};

export default useCollectionThoughts;
